import { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { match, Redirect, useHistory, useLocation, useParams } from 'react-router-dom';
import { ProductBox } from '../../components/ProductListing/ProductBox/ProductBox';
import { generateUUID, renderTemplate } from '../../components/utils';
import { useConfig } from '../../config';
import { CmsIndex, hasCmsFile } from '../../utils/cmsIndex';
import { previewedFile } from '../../utils/draftPreview';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useCriteo } from '../../hooks/useCriteo';
import { useRedirectAlerts } from '../../hooks/useRedirectAlerts';
import { useRedirectOnError } from '../../hooks/useRedirectOnError';
import { useTranslation } from '../../i18n/i18n';
import { fetchResourceMap, isRedirect, optionalResource, Res, useApi, useCms } from '../../state/api';
import { CategoryTreeContainer } from '../../state/categories';
import {
	FILTER_MULTI_VALUE_DELIMITER,
	FILTER_RANGE_VALUE_DELIMITER,
	FilterContainer,
	isRangeFacetApplied as isRangeFacetAppliedStatic,
	resolveRangeSelectionForFacet,
} from '../../state/filters';
import { MetaContainer } from '../../state/meta';
import { useSession } from '../../state/session';
import { UiContainer } from '../../state/ui';
import {
	ApiCategory,
	ApiCategoryMetaData,
	ApiCategoryProductsLoop54,
	ApiFacetLoop54,
	ApiProduct,
	Attribute,
	FacetPayloadLoop54,
	Loop54CategoryProductsRequestBody,
	Loop54SortPayload,
} from '../../types';
import { Breadcrumbs } from '../Breadcrumbs/Breadcrumbs';
import { CmsComponent, JsxArgs } from '../cms/CmsComponent';
import { ExpandableBlock } from '../ExpandableBlock/ExpandableBlock';
import { FilterPanel } from '../FilterPanel/FilterPanel';
import { Main } from '../Main/Main';
import { FilterBadges } from '../Pagination/FilterBadges/FilterBadges';
import { FilterButton } from '../Pagination/FilterButton/FilterButton';
import { PagesLoop54 } from '../Pagination/PaginationLoop54';
import { SortingChooser } from '../Pagination/SortingChooser/SortingChooser';
import { RawHtml } from '../RawHtml';
import { RedirectStatus } from '../RedirectStatus';
import { parsePagesQueryLoop54, Routing, useFilterQueryParams, useQuery } from '../Routes';
import { SubcategoryLinks } from '../SubcategoryLinks/SubcategoryLinks';
import './CategoryPage.css';
import { ErrorPage } from './ErrorPage';
import { NotFoundPage } from './NotFoundPage';

export const CategoryPageLoop54 = (): JSX.Element => {
	const { perma } = useParams<UrlParams>();
	const { sort, price, brand, inStock, campaign, isEco } = useQuery<{
		sort: string;
		price: string;
		brand: string;
		inStock: string;
		campaign: string;
		isEco: string;
	}>();
	const { getName } = CategoryTreeContainer.useContainer();
	const { data: categoryMetaData, error: categoryMetaDataError } = useApi(Res.categoryMetaData, {
		perma,
	});
	const { gridBannerCategoryPermas = [], domain } = useConfig();
	const { getSupercategoryId, permaIndexedCategories } = CategoryTreeContainer.useContainer();
	const supercategoryId = getSupercategoryId(perma ?? '');
	const { sendEventOnce } = useAnalytics();
	const { sendCriteoEventOnce, criteoId } = useCriteo();
	const { user } = useSession();
	const { setFilters, clearFilters, filters } = FilterContainer.useContainer();
	const t = useTranslation().category;
	const { routes } = useTranslation();
	const query = useQuery<{ page?: string }>();
	const ui = UiContainer.useContainer();
	const { isFilterButtonsSticky } = ui;
	const page = +(query.page ?? '1') || 1;
	const absolutePath = `https://${domain}/${perma}`;
	const canonicalUrl = page && page > 1 ? `${absolutePath}?page=${page}` : absolutePath;
	const limit = 26;
	const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
	const [isClient, setIsClient] = useState(false);
	const hasDescription = categoryMetaData && !isRedirect(categoryMetaData) && categoryMetaData.description.length > 0;
	const shouldRenderDescription = hasDescription && (page <= 1 || isClient);
	const offset = 0;

	// Mobile row offset logic for banner positioning
	const getMobileRowWithOffset = (originalRow: number) => {
		let offset = 2; // Base offset for mobile (other elements render first)
		if (!shouldRenderDescription) {
			offset -= 1; // Reduce offset if no description
		}
		return originalRow + offset;
	};

	// Function to apply mobile offset to CMS data structure
	const applyMobileOffsetToCmsData = (cmsData: JsxArgs, offsetFunction: (row: number) => number): JsxArgs => {
		if (!Array.isArray(cmsData)) return cmsData;

		const [componentName, props, ...children] = cmsData;

		// If this is a GridBannerPlacement component, apply offset to mobile coordinates
		if (componentName === 'GridBannerPlacement' && props && typeof props === 'object') {
			const rawMobileStartRow = (props as any).mobileStartRow;
			const mobileStartRowNum =
				typeof rawMobileStartRow === 'string' ? Number(rawMobileStartRow) : (rawMobileStartRow as number);

			const updatedProps = {
				...props,
				// Shift the mobile start row by the calculated offset; height stays the same
				mobileStartRow: String(offsetFunction(mobileStartRowNum)),
			};
			return [
				componentName,
				updatedProps,
				...children.map((child) =>
					Array.isArray(child) ? applyMobileOffsetToCmsData(child, offsetFunction) : child
				),
			];
		}

		// Recursively process children for nested structures
		return [
			componentName,
			props,
			...children.map((child) =>
				Array.isArray(child) ? applyMobileOffsetToCmsData(child, offsetFunction) : child
			),
		];
	};
	useRedirectAlerts();
	const { searchProfileId, randomizeSearchProfileId } = MetaContainer.useContainer();
	const categoryId = permaIndexedCategories[perma]?.id;
	const attributes = permaIndexedCategories[perma]?.attributes;
	const history = useHistory();
	const filtersRef = useRef(filters);
	const previousPermaCleanupRef = useRef(perma);
	const [filtersActive, setFiltersActive] = useState(true);

	useEffect(() => {
		setIsClient(true);
	}, []);

	useEffect(() => {
		filtersRef.current = filters;
	}, [filters]);

	const loop54PostHeaders = {
		'Api-Version': 'V3',
		'User-id': randomizeSearchProfileId ? generateUUID() : searchProfileId,
	};
	const { getSubcategories } = CategoryTreeContainer.useContainer();
	const showFilterPanel = getSubcategories(perma).length === 0;
	const location = useLocation();
	const filterQueryparams = useFilterQueryParams(location.search);
	const hasQueryFilters =
		!!sort || !!price || !!brand || !!inStock || !!campaign || !!isEco || Object.keys(filterQueryparams).length > 0;
	const hasSelectedFilters = filters.some((filter) => {
		if (filter.type === 'distinct' && filter.items) {
			return filter.items.some((item) => item.selected);
		}
		if (filter.type === 'range') {
			return isRangeFacetAppliedStatic(filter);
		}
		return false;
	});
	const { data: cmsIndex } = useCms(Res.cmsIndex, {});
	const { search } = useLocation();
	// a draft of a file never published is in nobody's index, so the preview link is what says to ask
	const previewingThis = previewedFile(search) === Res.categoryCmsContent.uri({ perma });
	const shouldFetchCms =
		!hasQueryFilters &&
		!hasSelectedFilters &&
		(previewingThis || hasCmsFile(cmsIndex, 'categories', perma, gridBannerCategoryPermas));
	const { data: categoryCmsData } = useCms(shouldFetchCms ? Res.categoryCmsContent : null, { perma });

	useEffect(() => {
		if (previousPermaCleanupRef.current === perma) {
			return;
		}

		const isBackNavigation = history.action === 'POP';
		previousPermaCleanupRef.current = perma;

		if (isBackNavigation) {
			return;
		}

		const previousFilters = filtersRef.current;

		if (previousFilters.length > 0) {
			const searchParams = new URLSearchParams(location.search);
			let updated = false;

			previousFilters.forEach((filter) => {
				if (searchParams.has(filter.name)) {
					searchParams.delete(filter.name);
					updated = true;
				}
			});

			if (updated) {
				const newSearch = searchParams.toString();
				history.replace({
					pathname: location.pathname,
					search: newSearch ? `?${newSearch}` : '',
				});
			}
		}

		setFiltersActive(false);
		clearFilters();
	}, [perma, history, location.pathname, location.search, clearFilters]);

	const loop54CategoryProductsPayload = useMemo(() => {
		const shouldApplyFilters = filtersActive && previousPermaCleanupRef.current === perma;
		const filtersToApply = shouldApplyFilters ? filters : [];

		return buildLoopPayload(
			categoryId,
			attributes,
			sort,
			limit,
			offset,
			filtersToApply,
			filterQueryparams,
			price,
			brand,
			inStock,
			campaign,
			isEco,
			categoryMetaData && !isRedirect(categoryMetaData) ? categoryMetaData.attributes : undefined
		);
	}, [
		categoryId,
		attributes,
		sort,
		limit,
		offset,
		filters,
		filtersActive,
		perma,
		filterQueryparams,
		price,
		brand,
		inStock,
		campaign,
		isEco,
		categoryMetaData,
	]);

	const { loop54Api } = useConfig();
	const { data: categoryProductsData } = useApi(
		Res.categoryProductsLoop54,
		{
			perma,
			limit,
			offset,
			sort: sort ?? '',
			price: price ?? '',
			brand: brand ?? '',
			inStock: inStock ?? '',
			campaign: campaign ?? '',
			isEco: isEco ?? '',
			...filterQueryparams,
		},
		loop54Api,
		loop54CategoryProductsPayload,
		loop54PostHeaders,
		'POST'
	);

	useEffect(() => {
		if (categoryProductsData) {
			setFilters(categoryProductsData.results.facets);
			setFiltersActive(true);
		}
	}, [categoryProductsData]);

	useEffect(() => {
		if (categoryMetaData && 'description' in categoryMetaData && categoryMetaData.description.length > 0) {
			ui.setHasDescription(true);
		} else {
			ui.setHasDescription(false);
		}
	}, [categoryMetaData]);

	function handleSendEvent(products: ApiProduct[]) {
		if (!categoryMetaData || isRedirect(categoryMetaData)) return;
		sendEventOnce(
			{
				event: 'view_item_list',
				ecommerce: {
					item_list_id: String(categoryMetaData.id),
					item_list_name: categoryMetaData?.name,

					items: products.map((p) => ({
						item_id: String(p.id),
						item_name: p.name,
						price: p.price,
						list_name: 'Category',
						category: categoryMetaData?.name,
					})),
				},
			},
			perma
		);
	}
	function highlightId(isFirstPage: boolean, data: ApiCategoryProductsLoop54): number | null {
		// only highlight prooduct on first page
		if (!isFirstPage) return null;
		// only highlight product for "Popular" sorting
		if (sort) return null;
		// only highlight product on a category without subcategories
		if (getSubcategories(perma).length > 0) return null;
		// only highlight when no filters are applied
		if (data.results.facets.some((f) => f.type === 'distinct' && f.items?.some((i) => i.selected))) return null;
		return data.results.items[0]?.id;
	}

	// Apply mobile offset logic to CMS data if available
	const processedCmsData =
		shouldFetchCms && categoryCmsData ? applyMobileOffsetToCmsData(categoryCmsData, getMobileRowWithOffset) : null;

	// Render CMS banners if available
	const extraMarkup = processedCmsData ? [<CmsComponent key="cms-banners" structure={processedCmsData} />] : [];

	const { redirect, notFound } = useRedirectOnError(categoryMetaDataError);

	if (redirect) return <RedirectStatus to={redirect.newUrl} status={redirect.code} />;
	if (notFound) return <NotFoundPage />;

	if (user?.specialsOnly) return <Redirect to={routes.specialPrices} />;
	if (isRedirect(categoryMetaData))
		return <RedirectStatus to={categoryMetaData.newUrl} status={categoryMetaData.code} />;
	if (categoryMetaDataError) return <ErrorPage />;

	const title =
		categoryMetaData?.assets.title && categoryMetaData.assets.title != ''
			? categoryMetaData.assets.title + t.titleShortSuffix
			: (categoryMetaData?.name ?? getName(perma)) + t.titleSuffix;

	const metaDescription =
		categoryMetaData?.assets.metaDescription && categoryMetaData.assets.metaDescription != ''
			? categoryMetaData.assets.metaDescription
			: renderTemplate(t.metaDesc, { categoryName: categoryMetaData?.name ?? getName(perma) });

	const h1 =
		categoryMetaData?.assets.h1 && categoryMetaData.assets.h1 != ''
			? categoryMetaData.assets.h1
			: categoryMetaData?.name ?? getName(perma);

	let descriptionPane =
		!categoryMetaData || !shouldRenderDescription ? null : (
			<DescriptionPane descriptionData={categoryMetaData} onExpandChange={setIsDescriptionExpanded} />
		);

	const productsPreamble = (
		<>
			{descriptionPane}
			<div className="mobile-buttons">
				<div className={`filter-wrapper ${isFilterButtonsSticky ? 'sticky' : ''}`}>
					{showFilterPanel ? <FilterButton /> : <div></div>}
					<SortingChooser perma={perma} />
				</div>
			</div>
		</>
	);

	return (
		<Main>
			<Helmet>
				<title>
					{title}
					{page > 1 ? ' - ' + t.page + ' ' + page : ''}
				</title>
				<meta name="description" content={metaDescription} />
				<meta name="keywords" content={h1} />
				<link rel="canonical" href={canonicalUrl} />
			</Helmet>
			<div className={`category-page supercategory-${supercategoryId}`}>
				<Breadcrumbs perma={perma} />
				<SubcategoryLinks perma={perma} />
				<div className="heading-filter-grid">
					<h1 className="category-heading">
						{h1}
						{page > 1 ? ' - ' + t.page + ' ' + page : ''}
					</h1>
					<div className="filter-wrapper desktop">
						<FilterBadges hits={categoryProductsData?.results.count} perma={perma} />
						{showFilterPanel ? <FilterButton /> : <div></div>}
						<SortingChooser perma={perma} />
					</div>
				</div>
				<FilterPanel
					perma={perma}
					facets={categoryProductsData?.results.facets}
					filterPage="category"
					filterEventData={{ categoryId }}
					sortOverride={
						categoryMetaData && !isRedirect(categoryMetaData) ? categoryMetaData.attributes : undefined
					}
				/>
				<PagesLoop54
					resource={Res.categoryProductsLoop54}
					params={{
						perma,
						sort: sort ?? '',
						price: price ?? '',
						brand: brand ?? '',
						inStock: inStock ?? '',
						campaign: campaign ?? '',
						isEco: isEco ?? '',
						...filterQueryparams,
					}}
					onRender={(data) => {
						if (!!criteoId) {
							sendCriteoEventOnce(
								{ event: 'viewList', item: data.results.items.map((p) => p.id) },
								perma
							);
						}
						handleSendEvent(data.results.items);
					}}
					preamble={productsPreamble}
					buildChildren={(data, isFirstPage) => {
						return (
							<>
								{data.results.items.map((p) => (
									<ProductBox
										key={p.id}
										product={p}
										highlight={p.id === highlightId(isFirstPage, data)}
									/>
								))}
							</>
						);
					}}
					postPayload={loop54CategoryProductsPayload}
					postHeaders={loop54PostHeaders}
					isCategoryPage
					hasDescription={shouldRenderDescription}
					isDescriptionExpanded={isDescriptionExpanded}
					extraMarkup={extraMarkup}
				/>
			</div>
		</Main>
	);
};

type DescriptionPaneProps = {
	descriptionData: ApiCategoryMetaData;
	onExpandChange: (isExpanded: boolean) => void;
};
export const DescriptionPane = ({ descriptionData, onExpandChange }: DescriptionPaneProps) => {
	const { getName } = CategoryTreeContainer.useContainer();
	const { perma } = useParams<UrlParams>();
	const [expanded, setExpanded] = useState(false);
	const t = useTranslation().category;

	return (
		<div className={'description-big-block ' + (expanded ? 'expanded' : '')}>
			<ExpandableBlock
				maxHeight={300}
				key={descriptionData.description}
				onUpdate={(isExpanded: boolean) => {
					setExpanded(isExpanded);
					onExpandChange(isExpanded);
				}}
				item={
					<>
						<h2 className="description-heading">
							{descriptionData.name ?? getName(perma)}: {t.buyGuide}
						</h2>
						<RawHtml html={descriptionData.description} className="description-text" />
					</>
				}
			/>
		</div>
	);
};

export type UrlParams = { perma: string };

export const categoryRouting: Routing<UrlParams> = {
	path(_urls) {
		return '/:perma(.*-c-.*|c/.*)';
	},
	component: CategoryPageLoop54,
	requiresAuth: false,
	// the same as the api allows for categories/by-perma
	cacheMaxAge: 3600,
	preload: async (
		config,
		routeMatch: match<UrlParams>,
		query: string,
		permaIndexedCategories?: Record<string, ApiCategory>,
		cmsIndex?: CmsIndex | null
	) => {
		const { perma } = routeMatch.params;
		let page = parsePagesQueryLoop54(query);
		let { sort, limit, offset, price, brand, inStock, campaign, isEco } = page;
		const isFirstPage = offset === 0;
		if (isFirstPage) {
			limit = 26;
			page.limit = 26;
			offset = 0;
			page.offset = 0;
		} else {
			limit = 24;
			page.limit = 24;
			page.offset += 2;
			offset += 2;
		}
		const filterQueryparams = useFilterQueryParams(query);

		if (permaIndexedCategories) {
			const categoryId = permaIndexedCategories[perma]?.id;
			const attributes = permaIndexedCategories[perma]?.attributes;
			const { loop54Api, cmsContent, gridBannerCategoryPermas = [] } = config;
			const shouldFetchCms =
				previewedFile(query) === Res.categoryCmsContent.uri({ perma }) ||
				hasCmsFile(cmsIndex, 'categories', perma, gridBannerCategoryPermas);

			const loop54PostHeaders = {
				'Api-Version': 'V3',
				'User-id': generateUUID(), // use randomized id in ssr
			};
			let loop54CategoryProductsPayload: Loop54CategoryProductsRequestBody = buildLoopPayload(
				categoryId,
				attributes,
				sort,
				limit,
				offset,
				[], // only used in client side
				filterQueryparams,
				price,
				brand,
				inStock,
				campaign,
				isEco,
				undefined // sortOverride not available in SSR
			);

			const assets: Record<string, unknown>[] = await Promise.all([
				fetchResourceMap(
					config,
					Res.categoryProductsLoop54,
					{ perma, ...page, ...filterQueryparams },
					loop54Api,
					loop54CategoryProductsPayload,
					loop54PostHeaders,
					'POST'
				),
				fetchResourceMap(config, Res.categoryMetaData, { perma }),
				...(shouldFetchCms
					? [optionalResource(fetchResourceMap(config, Res.categoryCmsContent, { perma }, cmsContent))]
					: []),
			]);
			return Object.assign({}, ...assets);
		}
	},
};

export const buildLoopPayload = (
	categoryId: number,
	_attributes: string[] | undefined, // might use later
	sort: string | undefined,
	limit: number,
	offset: number,
	filters: ApiFacetLoop54[],
	filterParams: Record<string, string>, // dynamic filter query params
	price?: string,
	brand?: string,
	inStock?: string,
	campaign?: string,
	isEco?: string,
	sortOverride?: Attribute[]
): Loop54CategoryProductsRequestBody => {
	let facets: FacetPayloadLoop54[] = [
		{
			name: 'price',
			attributeName: 'price',
			type: 'range',
		},
		{
			name: 'brand',
			attributeName: 'brand',
			type: 'distinct',
			sortBy: [
				{
					type: 'item',
					order: 'asc',
				},
			],
		},
		{
			name: 'inStock',
			attributeName: 'inStock',
			type: 'distinct',
			sortBy: [
				{
					type: 'item',
					order: 'asc',
				},
			],
		},
		{
			name: 'campaign',
			attributeName: 'campaign',
			type: 'distinct',
			sortBy: [
				{
					type: 'item',
					order: 'asc',
				},
			],
		},
		{
			name: 'isEco',
			attributeName: 'isEco',
			type: 'distinct',
			sortBy: [
				{
					type: 'item',
					order: 'asc',
				},
			],
		},
	];

	// Add dynamic facets from filters based on sortOverride
	if (filters && sortOverride && sortOverride.length > 0) {
		// Filter filters that have matching sortOverride
		const matchingFilters = filters.filter((filter) => {
			const attributeNumber = filter.name.split('_')[0];
			return sortOverride.some((sortItem) => attributeNumber === sortItem.attribute.toString());
		});

		// Separate filters by sort value
		const filtersWithSort = matchingFilters.filter((filter) => {
			const attributeNumber = filter.name.split('_')[0];
			const sortItem = sortOverride.find((item) => attributeNumber === item.attribute.toString());
			return sortItem && sortItem.sort > 0;
		});

		const filtersWithSortZero = matchingFilters.filter((filter) => {
			const attributeNumber = filter.name.split('_')[0];
			const sortItem = sortOverride.find((item) => attributeNumber === item.attribute.toString());
			return sortItem && sortItem.sort === 0;
		});

		// Sort filters with sort > 0 by sort number (ascending)
		const sortedFiltersWithSort = filtersWithSort.sort((a, b) => {
			const aSortItem = sortOverride.find((item) => {
				const attributeNumber = a.name.split('_')[0];
				return attributeNumber === item.attribute.toString();
			});
			const bSortItem = sortOverride.find((item) => {
				const attributeNumber = b.name.split('_')[0];
				return attributeNumber === item.attribute.toString();
			});

			if (aSortItem && bSortItem) {
				return aSortItem.sort - bSortItem.sort;
			}
			return 0;
		});

		// Sort filters with sort = 0 by id (ascending)
		const sortedFiltersWithSortZero = filtersWithSortZero.sort((a, b) => {
			const aSortItem = sortOverride.find((item) => {
				const attributeNumber = a.name.split('_')[0];
				return attributeNumber === item.attribute.toString();
			});
			const bSortItem = sortOverride.find((item) => {
				const attributeNumber = b.name.split('_')[0];
				return attributeNumber === item.attribute.toString();
			});

			if (aSortItem && bSortItem) {
				return aSortItem.id - bSortItem.id;
			}
			return 0;
		});

		// Combine: first filters with sort > 0, then filters with sort = 0
		const sortedFilters = [...sortedFiltersWithSort, ...sortedFiltersWithSortZero];

		// Convert sorted filters to facets and append to existing facets
		sortedFilters.forEach((filter) => {
			const newFacet: FacetPayloadLoop54 = {
				name: filter.name,
				attributeName: filter.name,
				type: filter.type,
				...(filter.type === 'distinct' && {
					sortBy: [
						{
							type: 'item',
							order: 'asc',
						},
					],
				}),
			};
			facets.push(newFacet);
		});
	}

	// this isn't needed right now but perhaps we want to use it later
	/* 	// dynamically add facets based on category attributes
	if (attributes) {
		let dynamicFacets: FacetPayloadLoop54[] = attributes.map((a) => ({
			name: a,
			attributeName: a,
			type: 'distinct',
			sortBy: [
				{
					type: 'item',
					order: 'asc',
				},
			],
		}));
		facets = [...facets, ...dynamicFacets];
	} */

	// custom facets that came from loop api response
	if (filters && undefined) {
		const unmatchedFacets = filters.filter((filter) => !facets.some((facet) => facet.name === filter.name));
		unmatchedFacets.forEach((filter) => {
			facets.push({
				name: filter.name,
				type: filter.type,
				attributeName: filter.name,
				// we'll populate selected below through filterParams
				/* selected: [], */
				sortBy: [
					{
						type: 'item',
						order: 'asc',
					},
				],
			});
		});
	}

	// Process client-side filters from FilterContainer
	if (filters && filters.length > 0) {
		filters.forEach((filter) => {
			if (filter.type === 'distinct' && filter.items) {
				const appliedItems = filter.items.filter((item) => item.selected).map((item) => item.item);
				if (appliedItems.length > 0) {
					const correspondingFacet = facets.find((facet) => facet.name === filter.name);
					if (correspondingFacet) {
						correspondingFacet.selected = appliedItems;
					} else {
						// Add new distinct facet
						const newFacet: FacetPayloadLoop54 = {
							name: filter.name,
							attributeName: filter.name,
							type: 'distinct',
							selected: appliedItems,
							sortBy: [
								{
									type: 'item',
									order: 'asc',
								},
							],
						};
						facets.push(newFacet);
					}
				}
			} else if (filter.type === 'range') {
				const rangeSelection = resolveRangeSelectionForFacet(filter);
				if (rangeSelection) {
					const correspondingFacet = facets.find((facet) => facet.name === filter.name);
					if (correspondingFacet) {
						correspondingFacet.selected = { min: rangeSelection.min, max: rangeSelection.max };
					} else {
						// Add new range facet
						const newFacet: FacetPayloadLoop54 = {
							name: filter.name,
							attributeName: filter.name,
							type: 'range',
							selected: { min: rangeSelection.min, max: rangeSelection.max },
						};
						facets.push(newFacet);
					}
				}
			}
		});
	}

	// filter params for ssr of dynamic filters (only process if no client-side filters)
	const hasClientSideFilters =
		filters &&
		filters.some((filter) => {
			if (filter.type === 'distinct' && filter.items) {
				return filter.items.some((item) => item.selected);
			}
			if (filter.type === 'range') {
				return isRangeFacetAppliedStatic(filter);
			}
			return false;
		});
	if (!hasClientSideFilters) {
		Object.entries(filterParams).forEach(([key, value]) => {
			const correspondingFacet = facets.find((facet) => facet.name === key);

			if (correspondingFacet) {
				const selectedValues = multivalueFiltersToStrArray(value);
				if (selectedValues.length > 0) {
					correspondingFacet.selected = selectedValues;
				}
			} else {
				// dynamic facets
				const facetType = value.includes(FILTER_RANGE_VALUE_DELIMITER) ? 'range' : 'distinct';
				const newFacet: FacetPayloadLoop54 = {
					name: key,
					attributeName: key,
					type: facetType,
					selected:
						facetType === 'distinct' ? multivalueFiltersToStrArray(value) : rangeValueToFacetObj(value),
					...(facetType === 'distinct' && {
						sortBy: [
							{
								type: 'item',
								order: 'asc',
							},
						],
					}),
				};
				facets.push(newFacet);
			}
		});
	}

	// handle default facets
	if (brand) {
		const appliedValues = brand.split(FILTER_MULTI_VALUE_DELIMITER).map((v) => v.trim());
		const correspondingFacet = facets.find((facet) => facet.name === 'brand');
		if (correspondingFacet) {
			correspondingFacet.selected = appliedValues;
		} else {
			const brandFacet: FacetPayloadLoop54 = {
				name: 'brand',
				attributeName: 'brand',
				type: 'distinct',
				selected: appliedValues,
				sortBy: [
					{
						type: 'item',
						order: 'asc',
					},
				],
			};
			facets.push(brandFacet);
		}
	}

	if (price) {
		const facetRangeObj = rangeValueToFacetObj(price);
		const correspondingFacet = facets.find((facet) => facet.name === 'price');

		if (correspondingFacet) {
			correspondingFacet.selected = facetRangeObj;
		} else {
			const priceFacet: FacetPayloadLoop54 = {
				name: 'price',
				attributeName: 'price',
				type: 'range',
				selected: facetRangeObj,
			};
			facets.push(priceFacet);
		}
	}

	if (inStock) {
		const correspondingFacet = facets.find((facet) => facet.name === 'inStock');
		if (correspondingFacet) {
			correspondingFacet.selected = ['true'];
		} else {
			const inStockFacet: FacetPayloadLoop54 = {
				name: 'inStock',
				attributeName: 'inStock',
				type: 'distinct',
				selected: ['true'],
				sortBy: [
					{
						type: 'item',
						order: 'asc',
					},
				],
			};
			facets.push(inStockFacet);
		}
	}

	if (campaign) {
		const correspondingFacet = facets.find((facet) => facet.name === 'campaign');
		if (correspondingFacet) {
			correspondingFacet.selected = ['true'];
		} else {
			const campaignFacet: FacetPayloadLoop54 = {
				name: 'campaign',
				attributeName: 'campaign',
				type: 'distinct',
				selected: ['true'],
				sortBy: [
					{
						type: 'item',
						order: 'asc',
					},
				],
			};
			facets.push(campaignFacet);
		}
	}

	if (isEco) {
		const correspondingFacet = facets.find((facet) => facet.name === 'isEco');
		if (correspondingFacet) {
			correspondingFacet.selected = [true];
		} else {
			const isEcoFacet: FacetPayloadLoop54 = {
				name: 'isEco',
				attributeName: 'isEco',
				type: 'distinct',
				selected: ['true'],
				sortBy: [
					{
						type: 'item',
						order: 'asc',
					},
				],
			};
			facets.push(isEcoFacet);
		}
	}

	let loop54CategoryProductsPayload: Loop54CategoryProductsRequestBody = {
		attribute: {
			name: 'categoryIds',
			value: categoryId + '',
		},
		resultsOptions: {
			facets,
			take: limit,
			skip: offset,
		},
	};
	loop54CategoryProductsPayload.resultsOptions.sortBy = determineSortPayload(sort);

	return loop54CategoryProductsPayload;
};
// a product's max ecoCount can be 9, so when filtering on eco products we want all products where ecoCount !== 0
export const maxEcoCount = [1, 2, 3, 4, 5, 6, 7, 8, 9];

export const rangeValueToFacetObj = (range: string) => {
	const [minStr, maxStr] = range.split(FILTER_RANGE_VALUE_DELIMITER);
	const min = Number(minStr);
	const max = Number(maxStr);

	return { min, max };
};

export const multivalueFiltersToStrArray = (values: string) => {
	return values.split(FILTER_MULTI_VALUE_DELIMITER);
};

export const determineSortPayload = (sort: string | undefined): Loop54SortPayload[] => {
	const defaultSorting: Loop54SortPayload[] = [
		{
			type: 'attribute',
			attributeName: 'recommended',
			order: 'desc',
		},
		{
			type: 'attribute',
			attributeName: 'campaign',
			order: 'desc',
		},
		{
			type: 'attribute',
			attributeName: 'inStock',
			order: 'desc',
		},
		{
			type: 'attribute',
			attributeName: 'boost',
			order: 'desc',
		},
	];

	if (sort) {
		let selectedSortPayload: Loop54SortPayload = { type: 'attribute' };
		switch (sort) {
			case 'aPrice':
				selectedSortPayload.attributeName = 'price';
				selectedSortPayload.order = 'asc';
				break;
			case 'dPrice':
				selectedSortPayload.attributeName = 'price';
				selectedSortPayload.order = 'desc';
				break;
			case 'aName':
				selectedSortPayload.attributeName = 'name';
				selectedSortPayload.order = 'asc';
				break;
			case 'dEco':
				selectedSortPayload.attributeName = 'ecoCount';
				selectedSortPayload.order = 'desc';
				break;
			default:
				return defaultSorting;
		}
		return [selectedSortPayload, ...defaultSorting];
	}
	return defaultSorting;
};
